import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import type { ItemDraft, ItemType, ParseResult } from '../api/types';
import { messageOf, normalizeLinkInput, typeOptions } from '../lib/format';
import { t } from '../i18n';
import { Modal } from './Modal';
import { Cover } from './cards';
import { useToast } from './Toast';
import { Field, Select, TextArea, TextInput } from './ui';
import { IconAlert, IconCheck, IconLink } from './icons';

const EMPTY_DRAFT: ItemDraft = {
  url: '',
  title: '',
  description: '',
  imageUrl: '',
  type: 'other',
  status: '',
  author: '',
  personalNote: '',
};

interface AddItemDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: (id: string) => void;
  initialUrl?: string;
}

/**
 * The product's front door: paste a link → the parser proposes metadata → the user fixes it → save.
 * A failed parse never blocks saving; manual entry is always one tap away.
 */
export function AddItemDialog({ open, onClose, onSaved, initialUrl = '' }: AddItemDialogProps) {
  const toast = useToast();
  const [url, setUrl] = useState(initialUrl);
  const [draft, setDraft] = useState<ItemDraft>(EMPTY_DRAFT);
  const [parsed, setParsed] = useState<ParseResult | null>(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [duplicateId, setDuplicateId] = useState<string | null>(null);
  const [titleError, setTitleError] = useState<string | null>(null);
  const urlInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setUrl(initialUrl);
    setDraft(EMPTY_DRAFT);
    setParsed(null);
    setParseError(null);
    setDuplicateId(null);
    setTitleError(null);
    const timer = setTimeout(() => urlInput.current?.focus(), 60);
    return () => clearTimeout(timer);
  }, [open, initialUrl]);

  const fillFrom = (result: ParseResult) => {
    setParsed(result);
    setDraft({
      url: result.finalUrl || result.requestedUrl,
      title: result.title ?? '',
      description: result.description ?? '',
      imageUrl: result.imageUrl ?? '',
      type: result.type ?? 'other',
      status: '',
      author: result.author ?? '',
      personalNote: '',
      // The store answer rides back to the server, so saving does not read Steam a second time.
      game: result.game,
    });
  };

  const startOver = () => {
    setParsed(null);
    setDraft(EMPTY_DRAFT);
    setParseError(null);
    setDuplicateId(null);
  };

  async function runParse() {
    const value = normalizeLinkInput(url);
    if (!value) {
      setParseError(t('dialog.pasteLink'));
      return;
    }
    setParsing(true);
    setParseError(null);
    try {
      const result = await api.items.parse(value);
      fillFrom(result);
    } catch (error) {
      setParseError(messageOf(error, t('dialog.parseFailed')));
      // Still let the user type everything by hand against that URL.
      setDraft((current) => ({ ...current, url: value }));
      setParsed({
        requestedUrl: value,
        finalUrl: value,
        canonicalUrl: value,
        sourceName: null,
        sourceDomain: null,
        type: 'other',
        partial: true,
        metadata: {},
      });
    } finally {
      setParsing(false);
    }
  }

  async function save() {
    if (!draft.title.trim()) {
      setTitleError(t('dialog.titleRequired'));
      return;
    }
    setSaving(true);
    try {
      const created = await api.items.create({ ...draft, url: normalizeLinkInput(draft.url || url) });
      toast.success(t('dialog.added'));
      onSaved(created.id);
    } catch (error) {
      if (error instanceof ApiError && error.code === 'ITEM_DUPLICATE') {
        const detail = error.details?.itemId;
        setDuplicateId(typeof detail === 'string' ? detail : '');
      } else {
        toast.error(messageOf(error));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      title={parsed ? t('dialog.reviewTitle') : t('dialog.addLink')}
      description={parsed ? t('dialog.reviewBody') : t('dialog.addBody')}
      onClose={onClose}
      footer={
        parsed ? (
          <>
            <button type="button" className="btn btn--ghost" onClick={startOver} disabled={saving}>
              {t('dialog.anotherLink')}
            </button>
            <button type="button" className="btn btn--primary" onClick={save} disabled={saving}>
              {saving ? t('item.saving') : t('dialog.toLibrary')}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="btn btn--ghost" onClick={() => void runParse()} disabled={parsing}>
              {parsing ? t('dialog.reading') : t('dialog.fetchData')}
            </button>
            <button
              type="button"
              className="btn btn--outline"
              onClick={() => {
                setDraft({ ...EMPTY_DRAFT, url: url.trim() });
                setParsed({
                  requestedUrl: url.trim(),
                  finalUrl: url.trim(),
                  canonicalUrl: url.trim(),
                  sourceName: null,
                  sourceDomain: null,
                  type: 'other',
                  partial: true,
                  metadata: {},
                });
              }}
              disabled={!url.trim() || parsing}
            >
              {t('dialog.fillManual')}
            </button>
          </>
        )
      }
    >
      {!parsed ? (
        <Field label={t('item.url')} htmlFor="add-url" error={parseError ?? undefined}>
          <TextInput
            id="add-url"
            ref={urlInput}
            className="input"
            type="url"
            inputMode="url"
            autoComplete="off"
            placeholder="https://…"
            value={url}
            invalid={Boolean(parseError)}
            onChange={(event) => {
              setUrl(event.target.value);
              setParseError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void runParse();
              }
            }}
          />
        </Field>
      ) : (
        <div className="stack add-flow">
          <div className="row row--between">
            <span className="caps">
              <IconLink size={13} /> {parsed.sourceName ?? t('dialog.manualSource')}
            </span>
            {parsed.existingItemId ? (
              <Link className="small" to={`/app/items/${parsed.existingItemId}`}>
                {parsed.type === 'game' ? t('dialog.existsGame') : t('dialog.existsItem')}
              </Link>
            ) : null}
          </div>

          {parsed.warning ? (
            <p className="notice notice--warn">
              <IconAlert size={16} />
              <span>{parsed.warning}</span>
            </p>
          ) : parsed.sourceDomain ? (
            <p className="notice">
              <IconCheck size={16} />
              <span>{t('dialog.parsedOk')}</span>
            </p>
          ) : (
            <p className="notice">
              <IconCheck size={16} />
              <span>{t('dialog.manualOk')}</span>
            </p>
          )}

          <div className="preview">
            <Cover src={draft.imageUrl || null} title={draft.title} className="preview__cover cover--portrait" />
            <div className="stack-sm">
              <Field label={t('playlists.fieldTitle')} htmlFor="add-title" error={titleError ?? undefined}>
                <TextInput
                  id="add-title"
                  value={draft.title}
                  invalid={Boolean(titleError)}
                  placeholder={t('dialog.titlePlaceholder')}
                  onChange={(event) => {
                    setDraft({ ...draft, title: event.target.value });
                    setTitleError(null);
                  }}
                />
              </Field>
              <Field label={t('item.type')} htmlFor="add-type">
                <Select
                  id="add-type"
                  value={draft.type}
                  onChange={(event) => setDraft({ ...draft, type: event.target.value as ItemType })}
                >
                  {typeOptions().map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </div>

          <Field label={t('item.url')} htmlFor="add-url-2" hint={t('dialog.urlHint')}>
            <TextInput
              id="add-url-2"
              type="url"
              value={draft.url}
              onChange={(event) => setDraft({ ...draft, url: event.target.value })}
            />
          </Field>

          <Field label={t('playlists.fieldDescription')} htmlFor="add-desc">
            <TextArea
              id="add-desc"
              rows={3}
              value={draft.description}
              onChange={(event) => setDraft({ ...draft, description: event.target.value })}
            />
          </Field>

          <div className="row" style={{ alignItems: 'flex-start', gap: 'var(--sp-4)' }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <Field label={t('dialog.cover')} htmlFor="add-image" hint={t('dialog.coverHint')}>
                <TextInput
                  id="add-image"
                  type="url"
                  value={draft.imageUrl}
                  onChange={(event) => setDraft({ ...draft, imageUrl: event.target.value })}
                />
              </Field>
            </div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <Field label={t('item.author')} htmlFor="add-author">
                <TextInput
                  id="add-author"
                  value={draft.author}
                  onChange={(event) => setDraft({ ...draft, author: event.target.value })}
                />
              </Field>
            </div>
          </div>

          <Field label={t('item.note')} htmlFor="add-note" hint={t('item.noteHint')}>
            <TextArea
              id="add-note"
              rows={2}
              value={draft.personalNote}
              onChange={(event) => setDraft({ ...draft, personalNote: event.target.value })}
            />
          </Field>

          {duplicateId !== null ? (
            <p className="field__error">
              {draft.type === 'game' ? t('dialog.dupGame') : t('dialog.dupItem')}
              {duplicateId ? (
                <Link to={`/app/items/${duplicateId}`} style={{ marginLeft: 'var(--sp-2)' }}>
                  {t('dialog.open')}
                </Link>
              ) : null}
            </p>
          ) : null}

          {parseError ? <p className="field__error">{parseError}</p> : null}
        </div>
      )}
    </Modal>
  );
}
